/**
 * The footer's menus. Entries carry a stable `id` rather than a label: labels are translated by
 * the caller (footerStrings.ts) and reach the dropdown as a serializable map keyed by these ids.
 */

/** A menu entry that navigates, or one that runs an action (e.g. the cookie drawer). */
export type FooterMenuItem =
    | { id: string; to: string }
    | { id: string; onSelect: () => void };

export type FooterMenu = {
    items: FooterMenuItem[];
};

/** A menu's translated copy: the trigger's label and each entry's, by entry id. */
export type FooterMenuLabels = {
    menu: string;
    items: Record<string, string>;
};

export const RESOURCES_MENU: FooterMenu = {
    items: [
        { id: 'faqs', to: '/faqs' },
        { id: 'glossary', to: '/glossary' },
        { id: 'changelog', to: '/changelog' },
        // Temporary home: this content moves onto the About page when that design lands.
        { id: 'partnersAndVendors', to: '/partners-and-vendors' },
    ],
};

export const LEGAL_MENU: FooterMenu = {
    items: [
        { id: 'privacyPolicy', to: '/privacy-policy' },
        { id: 'termsOfUse', to: '/terms-of-use' },
        { id: 'cookieSettings', onSelect: () => window.Osano?.cm?.showDrawer?.() },
    ],
};

export const CONTACT_LINK = { to: '/contact-us' };

/** Heap attributes for a footer control. The click name stays English so reporting does not split by locale. */
export const footerTracking = (click: string, buttonName: string, loggedIn: boolean) => ({
    'data-track-click': click,
    'data-track-location': 'footer',
    'data-track-button-name': buttonName,
    'data-track-logged-in': loggedIn ? 'true' : 'false',
});

/** One entry per menu id. */
export const FOOTER_ITEM_TRACKING: Record<string, { click: string; buttonName: string }> = {
    faqs: { click: 'Footer FAQs Clicked', buttonName: 'footer-faqs' },
    glossary: { click: 'Footer Glossary Clicked', buttonName: 'footer-glossary' },
    changelog: { click: 'Footer Changelog Clicked', buttonName: 'footer-changelog' },
    partnersAndVendors: { click: 'Footer Partners and Vendors Clicked', buttonName: 'footer-partners-and-vendors' },
    privacyPolicy: { click: 'Footer Privacy Policy Clicked', buttonName: 'footer-privacy-policy' },
    termsOfUse: { click: 'Footer Terms of Use Clicked', buttonName: 'footer-terms-of-use' },
    cookieSettings: { click: 'Footer Cookie Settings Clicked', buttonName: 'footer-cookie-settings' },
};
